import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/travis/build/sgrishchenko/reselect-utils/node_modules/gatsby-theme-docz/src/base/Layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "composing-key-selector-creator"
    }}>{`Composing Key Selector Creator`}</h1>
    <h2 {...{
      "id": "main-purpose"
    }}>{`Main Purpose`}</h2>
    <p>{`There is a concept of `}<a parentName="p" {...{
        "href": "https://github.com/toomuchdesign/re-reselect#keyselector"
      }}>{`Key Selector`}</a>{` in `}<a parentName="p" {...{
        "href": "https://github.com/toomuchdesign/re-reselect"
      }}>{`re-reselect`}</a>{`. It is working approach, but there are some limitations in straight forward using of this solution. Look at this example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`const state = {
  persons: {
    1: {
      id: 1,
      firstName: 'Marry',
      secondName: 'Poppins',
    },
    2: {
      id: 2,
      firstName: 'Harry',
      secondName: 'Potter',
    },
  },
};

import { createCachedSelector } from 're-reselect';
import { prop } from 'reselect-utils';

const personsSelector = (state: State) => state.persons;

const personSelector = createCachedSelector(
  [personsSelector, prop<{ personId: number }>().personId()],
  (persons, personId) => persons[personId],
)({
  keySelector: prop<{ personId: number }>().personId(),
});

const fullNameSelector = createCachedSelector(
  [personSelector],
  ({ firstName, secondName }) => \`\${firstName} \${secondName}\`,
)({
  keySelector: prop<{ personId: number }>().personId(),
});
`}</code></pre>
    <p>{`As you can see, every time, when we want use `}<inlineCode parentName="p">{`personSelector`}</inlineCode>{`, we should declare `}<inlineCode parentName="p">{`keySelector`}</inlineCode>{`. Since `}<a parentName="p" {...{
        "href": "https://github.com/toomuchdesign/re-reselect/blob/master/CHANGELOG.md#330"
      }}>{`3.3.0 version`}</a>{` `}<a parentName="p" {...{
        "href": "https://github.com/toomuchdesign/re-reselect"
      }}>{`re-reselect`}</a>{` introduces special `}<a parentName="p" {...{
        "href": "https://github.com/toomuchdesign/re-reselect#keyselectorcreator"
      }}>{`Key Selector Creator`}</a>{` option. This option can help you reduce `}<inlineCode parentName="p">{`keySelector`}</inlineCode>{` boiler plate. Reselect Utils offer implementation of `}<inlineCode parentName="p">{`Key Selector Creator`}</inlineCode>{`: `}<inlineCode parentName="p">{`Composing Key Selector Creator`}</inlineCode>{`. This implementation just merges all `}<inlineCode parentName="p">{`Key Selecters`}</inlineCode>{` from selector dependencies and splits them by `}<inlineCode parentName="p">{`:`}</inlineCode>{` sign. So, previous example can be rewritten next way:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`import { prop, composingKeySelectorCreator } from 'reselect-utils';

const personSelector = createCachedSelector(
  [personsSelector, prop<{ personId: number }>().personId()],
  (persons, personId) => persons[personId],
)({
  keySelector: prop<{ personId: number }>().personId(),
});

const fullNameSelector = createCachedSelector(
  [personSelector],
  ({ firstName, secondName }) => \`\${firstName} \${secondName}\`,
)({
  keySelectorCreator: composingKeySelectorCreator,
});
`}</code></pre>
    <p>{`But `}<inlineCode parentName="p">{`Composing Key Selector Creator`}</inlineCode>{` don't detect props automatically, so every time, when you use some property in selector dependencies, you should declare it in `}<inlineCode parentName="p">{`Key Selector`}</inlineCode>{`. See next example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`import { prop, composingKeySelectorCreator } from 'reselect-utils';

const fullNameSelector = createCachedSelector(
  [personSelector, prop<{ prefix: string }>().prefix()],
  ({ firstName, secondName }, prefix) => \`\${prefix} \${firstName} \${secondName}\`,
)({
  keySelector: prop<{ prefix: number }>().prefix(),
  keySelectorCreator: composingKeySelectorCreator,
});
`}</code></pre>
    <p>{`Now `}<inlineCode parentName="p">{`fullNameSelector`}</inlineCode>{` receives two properties: `}<inlineCode parentName="p">{`personId`}</inlineCode>{` and `}<inlineCode parentName="p">{`prefix`}</inlineCode>{`. It will work next way:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`fullNameSelector.keySelector(state, {
  personId: 1,
  prefix: 'Mr.',
}); // => 'Mr.:1'

fullNameSelector.keySelector(state, {
  personId: 2,
  prefix: 'Ms.',
}); // => 'Ms.:2'
`}</code></pre>
    <h2 {...{
      "id": "key-selectors-composition"
    }}>{`Key Selectors Composition`}</h2>
    <p>{`If you want use few props in selector dependencies, you can use `}<inlineCode parentName="p">{`composeKeySelectors`}</inlineCode>{` helper:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`import {
  prop,
  composeKeySelectors,
  composingKeySelectorCreator,
} from 'reselect-utils';

const fullNameSelector = createCachedSelector(
  [
    personSelector,
    prop<{ prefix: string }>().prefix(),
    prop<{ postfix: string }>().postfix(),
  ],
  ({ firstName, secondName }, prefix, postfix) =>
    \`\${prefix} \${firstName} \${secondName} (\${postfix})\`,
)({
  keySelector: composeKeySelectors(
    prop<{ prefix: number }>().prefix(),
    prop<{ postfix: string }>().postfix(),
  ),
  keySelectorCreator: composingKeySelectorCreator,
});

fullNameSelector.keySelector(state, {
  personId: 1,
  prefix: 'Mr.',
  postfix: 'father',
}); // => 'Mr.:father:1'

fullNameSelector.keySelector(state, {
  personId: 2,
  prefix: 'Ms.',
  postfix: 'sister',
}); // => 'Ms.:sister:2'
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      